import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'

import { getSpendLensItemMetricCard } from '@/api/clarityLens'
import { formatMetric } from '@/hooks/clarity/util'
import { SpendLensItemMetricCardModel } from '@/types/clarityLens'
import { dateToString } from '@/utils'
import { getDateRange } from '@/utils/date'

type TrendDataModel = {
  prevMetricsQuarterly: SpendLensItemMetricCardModel
  prevMetricsYearly: SpendLensItemMetricCardModel
}

export default function useSpendLensItemMetricCard(
  startDate: string,
  endDate: string,
  branches: string,
  itemName: string
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [spendLensItemMetricCardData, setSpendLensItemMetricCardData] =
    useState<SpendLensItemMetricCardModel>()
  const [trendData, setTrendData] = useState<TrendDataModel>()

  const fetchData = useMemo(() => {
    return async () => {
      try {
        const { data } = await fetchSpendLensItemMetricCardData(
          branches,
          startDate,
          endDate,
          itemName
        )
        setSpendLensItemMetricCardData(data)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [branches, startDate, endDate, itemName])

  const fetchTrendData = useMemo(() => {
    return async () => {
      try {
        const qtdDate = getDateRange('quarter-to-date')
        const ytdDate = getDateRange('year-to-date')
        const qtdTrendingDate = qtdDate.trending as DateRange
        const ytdTrendingDate = ytdDate.trending as DateRange

        const [qtdData, ytdData] = await Promise.all([
          fetchSpendLensItemMetricCardData(
            branches,
            dateToString(qtdTrendingDate, 'from'),
            dateToString(qtdTrendingDate, 'to'),
            itemName
          ),
          fetchSpendLensItemMetricCardData(
            branches,
            dateToString(ytdTrendingDate, 'from'),
            dateToString(ytdTrendingDate, 'to'),
            itemName
          ),
        ])

        setTrendData({
          prevMetricsQuarterly: qtdData.data,
          prevMetricsYearly: ytdData.data,
        })
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [branches, itemName])

  const fetchAllData = useCallback(async () => {
    if (!itemName) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      await Promise.all([fetchData(), fetchTrendData()])
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [fetchData, fetchTrendData, itemName])

  const refetch = useCallback(() => {
    console.log('Refetching spend lens item metric card...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    spendLensItemMetricCardData,
    trendData,
    loading,
    error,
    refetch,
  }
}

async function fetchSpendLensItemMetricCardData(
  branches: string,
  startDate: string,
  endDate: string,
  itemName: string
): Promise<{
  data: SpendLensItemMetricCardModel
}> {
  try {
    const response = await getSpendLensItemMetricCard(
      branches,
      startDate,
      endDate,
      itemName
    )

    const data: SpendLensItemMetricCardModel = convertData(
      response.total_spend,
      response.spend_share_percentage,
      response.average_unit_price,
      response.active_sku_count,
      response.unit
    )

    return {
      data,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function convertData(
  totalSpend: number,
  spendSharePercentage: number,
  averageUnitPrice: number,
  activeSkuCount: number,
  unit: string
): SpendLensItemMetricCardModel {
  return {
    totalSpend: formatMetric(totalSpend, 2),
    spendSharePercentage: formatMetric(spendSharePercentage, 2),
    averageUnitPrice: formatMetric(averageUnitPrice, 2),
    activeSkuCount: formatMetric(activeSkuCount, 2),
    unit,
  }
}
